/**
 * useModalA11y — Modal accessibility hook
 *
 * Provides:
 * - Escape key closes modal
 * - Initial focus on first focusable element when opened
 *
 * Usage:
 *   const containerRef = useModalA11y(isOpen, onClose);
 *   // attach to modal content container:
 *   <div ref={containerRef} role="dialog" aria-modal="true">
 */

import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function useModalA11y(isOpen: boolean, onClose: () => void) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Initial focus
  useEffect(() => {
    if (!isOpen) return;
    const node = containerRef.current;
    if (!node) return;
    // Defer to allow render
    const t = window.setTimeout(() => {
      const first = node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (first) {
        first.focus();
      }
    }, 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  return containerRef;
}
